import React, { useState } from 'react';
import {
  Box,
  SpaceBetween,
  Checkbox,
  Select,
  Grid,
  Multiselect,
  Input,
  FormField
} from '@amzn/awsui-components-react';

const Val = ({ children }) => <Box variant="span" fontWeight="bold">{children}</Box>;

const HourlyTab = ({ isViewMode, onConfigChange, initialConfig = {} }) => {
  const [hourlyOption, setHourlyOption] = useState('interval');
  const [hourlyInterval, setHourlyInterval] = useState({ label: '1', value: '1' });
  const [selectedHours, setSelectedHours] = useState([]);
  const [hourlyRangeStart, setHourlyRangeStart] = useState({ label: '09:00', value: '9' });
  const [hourlyRangeEnd, setHourlyRangeEnd] = useState({ label: '17:00', value: '17' });
  const [specificMinute, setSpecificMinute] = useState('0');
  const [minuteError, setMinuteError] = useState('');
  const initialConfigKey = JSON.stringify(initialConfig);

  React.useEffect(() => {
    const config = Object.keys(initialConfig).length > 0 ? initialConfig : { option: 'interval', interval: '1', selectedHours: [], rangeStart: '9', rangeEnd: '17', specificMinute: '0' };
    setHourlyOption(config.option || 'interval');
    if (config.interval) setHourlyInterval({ label: config.interval, value: config.interval });
    if (config.selectedHours) setSelectedHours(config.selectedHours.map(hour => ({ label: `${hour.toString().padStart(2, '0')}:00`, value: hour.toString() })));
    if (config.rangeStart) setHourlyRangeStart({ label: `${config.rangeStart.toString().padStart(2, '0')}:00`, value: config.rangeStart.toString() });
    if (config.rangeEnd) setHourlyRangeEnd({ label: `${config.rangeEnd.toString().padStart(2, '0')}:00`, value: config.rangeEnd.toString() });
    if (config.specificMinute !== undefined) setSpecificMinute(config.specificMinute);
  }, [initialConfigKey]);

  const handleConfigChange = (overrides = {}) => {
    onConfigChange({
      option: hourlyOption,
      interval: hourlyInterval?.value,
      selectedHours: selectedHours.map(h => h.value),
      rangeStart: hourlyRangeStart?.value,
      rangeEnd: hourlyRangeEnd?.value,
      specificMinute,
      ...overrides
    });
  };

  const handleMinuteChange = (value) => {
    if (value === '') {
      setSpecificMinute('');
      setMinuteError('Minute is required');
      return;
    }
    if (!/^\d+$/.test(value) || value !== String(parseInt(value))) {
      return;
    }
    const numValue = parseInt(value);
    if (numValue >= 0 && numValue <= 59) {
      setSpecificMinute(value);
      setMinuteError('');
      handleConfigChange({ specificMinute: value });
    } else {
      setSpecificMinute(value);
      setMinuteError('Minute must be between 0 and 59');
    }
  };

  const hourOptions = [...Array(24)].map((_, i) => ({ label: `${i.toString().padStart(2, '0')}:00`, value: i.toString() }));
  const rangeStartOptions = hourOptions.filter(o => hourOptions.some(e => parseInt(e.value) > parseInt(o.value)));
  const filteredRangeEndOptions = hourOptions.filter(o => parseInt(o.value) > parseInt(hourlyRangeStart?.value));

  if (isViewMode) {
    return (
      <SpaceBetween size="s">
        <Box>
          <Box variant="awsui-key-label">Schedule type</Box>
          <Box>
            {hourlyOption === 'interval' && <span>Every <Val>{hourlyInterval?.label}</Val> hour(s)</span>}
            {hourlyOption === 'range' && <span>Every hour between <Val>{hourlyRangeStart?.label}</Val> and <Val>{hourlyRangeEnd?.label}</Val></span>}
            {hourlyOption === 'specific' && <span>Specific hours: <Val>{selectedHours.map(h => h.label).join(', ') || '—'}</Val></span>}
          </Box>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Run at minute</Box>
          <Val>{specificMinute}</Val>
        </Box>
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="m">
      <Box>
        <Checkbox
          checked={hourlyOption === 'interval'}
          onChange={({ detail }) => { if (detail.checked) { setHourlyOption('interval'); handleConfigChange({ option: 'interval' }); } }}
        >
          Every
        </Checkbox>
        {hourlyOption === 'interval' && (
          <Box margin={{ left: 'xxl', top: 'xs' }}>
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
              <Select
                selectedOption={hourlyInterval}
                onChange={({ detail }) => { setHourlyInterval(detail.selectedOption); handleConfigChange({ interval: detail.selectedOption.value }); }}
                options={[{ label: '1', value: '1' }, { label: '2', value: '2' }, { label: '3', value: '3' }]}
              />
              <Box padding={{ top: 'xs' }}>hour(s)</Box>
            </Grid>
          </Box>
        )}
      </Box>

      <Box>
        <Checkbox
          checked={hourlyOption === 'range'}
          onChange={({ detail }) => { if (detail.checked) { setHourlyOption('range'); handleConfigChange({ option: 'range' }); } }}
        >
          Run every hour between
        </Checkbox>
        {hourlyOption === 'range' && (
          <Box margin={{ left: 'xxl', top: 'xs' }}>
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 1 }, { colspan: 3 }]}>
              <Select
                selectedOption={hourlyRangeStart}
                onChange={({ detail }) => {
                  setHourlyRangeStart(detail.selectedOption);
                  if (parseInt(hourlyRangeEnd?.value) <= parseInt(detail.selectedOption.value)) {
                    const newEnd = hourOptions.find(o => parseInt(o.value) > parseInt(detail.selectedOption.value));
                    setHourlyRangeEnd(newEnd || null);
                    handleConfigChange({ rangeStart: detail.selectedOption.value, rangeEnd: newEnd?.value });
                  } else {
                    handleConfigChange({ rangeStart: detail.selectedOption.value });
                  }
                }}
                options={rangeStartOptions}
              />
              <Box padding={{ top: 'xs' }}>and</Box>
              <Select
                selectedOption={hourlyRangeEnd}
                onChange={({ detail }) => { setHourlyRangeEnd(detail.selectedOption); handleConfigChange({ rangeEnd: detail.selectedOption.value }); }}
                options={filteredRangeEndOptions}
              />
            </Grid>
          </Box>
        )}
      </Box>

      <Box>
        <Checkbox
          checked={hourlyOption === 'specific'}
          onChange={({ detail }) => { if (detail.checked) { setHourlyOption('specific'); handleConfigChange({ option: 'specific' }); } }}
        >
          Choose specific hours
        </Checkbox>
        {hourlyOption === 'specific' && (
          <Box margin={{ left: 'xxl', top: 'xs' }}>
            <Multiselect
              selectedOptions={selectedHours}
              onChange={({ detail }) => { setSelectedHours(detail.selectedOptions); handleConfigChange({ selectedHours: detail.selectedOptions.map(h => h.value) }); }}
              options={[...Array(24)].map((_, i) => ({ label: `${i.toString().padStart(2, '0')}:00`, value: i.toString() }))}
              placeholder="Choose hours (e.g., 09:00, 14:00, 18:00)"
            />
          </Box>
        )}
      </Box>

      <Box>
        <FormField label="Run at specific minute (0-59)" errorText={minuteError}>
          <Input
            value={specificMinute}
            onChange={({ detail }) => handleMinuteChange(detail.value)}
            placeholder="0-59"
          />
        </FormField>
      </Box>
    </SpaceBetween>
  );
};

export default HourlyTab;
